import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Breadcrumb } from '../components/Breadcrumb';
import { BreadcrumbSchema } from '../components/SchemaOrg';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';
import { articles } from '../data/articles';

const BlogArticle = () => {
  const { slug } = useParams();
  const article = articles[slug];

  if (!article) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-4xl font-bold mb-4">Article non trouvé</h1>
        <Button asChild data-testid="back-to-blog-btn">
          <Link to="/blog">Retour au blog</Link>
        </Button>
      </div>
    );
  }

  const breadcrumbItems = [
    { name: 'Blog', url: '/blog' },
    { name: article.title, url: `/blog/${article.slug}` }
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.dateISO,
    "dateModified": article.dateISO,
    "author": {
      "@type": "Organization",
      "name": "NHM Plomberie"
    },
    "publisher": {
      "@type": "Organization",
      "name": "NHM Plomberie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://nhm-plomberie.fr/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://nhm-plomberie.fr/blog/${article.slug}`
    }
  };

  return (
    <>
      <SEO
        title={article.seoTitle}
        description={article.seoDescription}
        keywords={article.seoKeywords}
        canonical={`https://nhm-plomberie.fr/blog/${article.slug}`}
        ogType="article"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://nhm-plomberie.fr/' },
        { name: 'Blog', url: 'https://nhm-plomberie.fr/blog' },
        { name: article.title, url: `https://nhm-plomberie.fr/blog/${article.slug}` }
      ]} />

      <Breadcrumb items={breadcrumbItems} />

      <article className="py-12" data-testid="blog-article">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Button asChild variant="ghost" className="mb-6" data-testid="back-to-blog-btn">
              <Link to="/blog" className="flex items-center gap-2">
                <ArrowLeft size={18} />
                Retour au blog
              </Link>
            </Button>

            <img
              src={article.image}
              alt={article.title}
              className="w-full h-96 object-cover rounded-xl mb-8"
            />

            <div className="flex items-center gap-4 text-gray-600 mb-4">
              <span className="flex items-center gap-2">
                <Calendar size={18} />
                {article.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock size={18} />
                {article.readTime} de lecture
              </span>
            </div>

            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-6">
              {article.category}
            </span>

            <h1 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900">
              {article.title}
            </h1>

            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 p-8 bg-blue-50 rounded-xl border-2 border-blue-600">
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Besoin d'un Plombier Professionnel ?</h3>
              <p className="text-gray-700 mb-6">
                NHM Plomberie intervient rapidement à Paris 17e et dans Paris et proche banlieue. 
                Disponibles 24h/24 pour toute urgence.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" data-testid="article-call-btn">
                  <a href="tel:0970705863">Appeler: 09 70 70 58 63</a>
                </Button>
                <Button asChild size="lg" variant="outline" data-testid="article-devis-btn">
                  <Link to="/contact">Demander un Devis</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogArticle;
